import { useCallback } from "react";
import { useNavigate } from "react-router";
import { assertNever } from "./assert-never";
import * as borogove from "borogove";
import { isTauri } from "@tauri-apps/api/core";
import {
	removeActive,
	sendNotification,
} from "@tauri-apps/plugin-notification";
import { href, readyServiceWorker } from "./util";

type ChatNotificationData = {
	accountId: string;
	chatId: string;
	messageId?: string | null;
};

export type NotificationData =
	| ({ type: borogove.MessageType } & ChatNotificationData)
	| { type: "loginRequest" };

const chatUrl = ({ accountId, chatId, messageId }: ChatNotificationData) =>
	href("/accounts/:accountId/chats/:chatId", { accountId, chatId }) +
	(messageId ? `#message:${messageId}` : "");

const onNotificationClose = (
	data: NotificationData,
	chat: borogove.Chat | null,
) => {
	switch (data.type) {
		case "loginRequest":
		case borogove.MessageType.MessageChat:
		case borogove.MessageType.MessageChannel:
		case borogove.MessageType.MessageChannelPrivate:
			return;
		case borogove.MessageType.MessageCall:
			if (chat?.callStatus() === borogove.calls.CallStatus.Incoming)
				chat.hangup();
			return;
		default:
			return assertNever(data);
	}
};

export const useNotifications = () => {
	const navigate = useNavigate();

	const onNotificationClick = useCallback(
		(data: NotificationData, chat: borogove.Chat | null) => {
			switch (data.type) {
				case "loginRequest":
					return;
				case borogove.MessageType.MessageChat:
				case borogove.MessageType.MessageChannel:
				case borogove.MessageType.MessageChannelPrivate:
					navigate(chatUrl(data));
					return;
				case borogove.MessageType.MessageCall:
					navigate(chatUrl(data));
					if (chat?.callStatus() === borogove.calls.CallStatus.Incoming)
						chat.acceptCall();
					return;
				default:
					return assertNever(data);
			}
		},
		[navigate],
	);

	const showNotification = useCallback<ShowNotification>(
		async (title, options, chat) => {
			const worker = await readyServiceWorker;

			if (worker) {
				await worker.showNotification(title, options);
				return null;
			} else if (isTauri()) {
				const id = crypto.getRandomValues(new Int32Array(1))[0];
				sendNotification({
					id,
					title,
					body: options.body,
					extra: options.data,
				});
				return { close: () => removeActive([{ id }]) };
			} else {
				const notification = new Notification(title, options);
				let clicked = false;
				notification.addEventListener("click", () => {
					clicked = true;
					onNotificationClick(options.data, chat);
				});

				notification.addEventListener("close", () => {
					if (!clicked) onNotificationClose(options.data, chat);
				});

				return notification;
			}
		},
		[onNotificationClick],
	);

	return { showNotification, onNotificationClick, onNotificationClose };
};

export type ShowNotification = (
	title: string,
	options: NotificationOptions & { data: NotificationData },
	chat: borogove.Chat | null,
) => Promise<Pick<Notification, "close"> | null>;
export const showMessageNotification = async (
	accountId: string,
	sender: borogove.Member,
	message: borogove.ChatMessage,
	chat: borogove.Chat,
	showNotification: ShowNotification,
) => {
	if (!Notification?.permission) return;

	const serviceWorker = await readyServiceWorker;
	const tag = accountId + ">" + message.chatId();
	if (serviceWorker) {
		// Check if we already notified this message
		const notifs = await serviceWorker.getNotifications({
			tag: tag,
		});
		for (const notif of notifs) {
			if (notif.data.messageId === (message.serverId || message.localId))
				return;
		}
	}

	const title = "Message from " + (sender?.displayName ?? message.chatId());
	let icon = sender.photoUri ?? sender.placeholderUri;
	if (!icon.startsWith("data:")) {
		const response = await fetch(icon);
		const mime = response.headers.get("Content-Type");
		if (response.ok && mime) {
			const buffer = await response.arrayBuffer();
			const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
			icon = `data:${mime};base64,${base64}`;
		}
	}
	const body = message.body().toPlainText();
	const options = {
		body: !body && message.attachments.length > 0 ? "Media" : body,
		icon,
		tag,
		renotify: true,
		timestamp: new Date(message.timeSent).getTime(),
		vibrate: [50, 50, 50],
		data: {
			type: message.type,
			accountId,
			chatId: message.chatId(),
			messageId: message.serverId || message.localId,
		},
	};
	return showNotification(title, options, chat);
};

export const showCallNotification = async (
	accountId: string,
	chat: borogove.Chat,
	showNotification: ShowNotification,
) => {
	if (!Notification?.permission) return null;

	const serviceWorker = await readyServiceWorker;
	const tag = accountId + ">" + chat.chatId + ">call";
	if (serviceWorker) {
		const notifs = await serviceWorker.getNotifications({
			tag: tag,
		});
		if (notifs.length > 0) return null; // Already notified, probably from push
		// We could check session id here, but we only do one call at a time in UI anyway
	}
	const title = "Incoming Call";
	const options = {
		body: "From " + chat.getDisplayName(),
		tag: tag,
		vibrate: [50, 100, 50, 100],
		data: {
			accountId: accountId,
			chatId: chat.chatId,
			type: borogove.MessageType.MessageCall as const,
		},
	};

	return showNotification(title, options, chat);
};

export const showLoginRequestNotification = async (
	req: borogove.LoginRequest,
	showNotification: ShowNotification,
) => {
	if (!Notification?.permission) return null;

	const title = "Login Request";
	const options = {
		body: req.method + " " + req.url,
		tag: req.id,
		data: { type: "loginRequest" as const },
	};

	return showNotification(title, options, null);
};
